import { VideoEditorLogger } from '@src/utility/VideoEditorLogger';

import { EditorChangeType } from '../types';
import { AbstractCommand } from './AbstractCommand';

import type { Element } from '../models/Element';
import type { Track } from '../models/Track';
import type { VideoEditor } from '../VideoEditor';

export class RemoveTrackCommand extends AbstractCommand {
  private trackId: string;

  getName(): string {
    return 'RemoveTrackCommand';
  }

  private removedTrack?: Track;

  private trackElements: Element[] = [];

  constructor(editor: VideoEditor, trackOrId: Track | string) {
    super(editor);
    this.trackId = typeof trackOrId === 'string' ? trackOrId : trackOrId.id;
    // Store the track for undo/redo operations
    this.removedTrack =
      typeof trackOrId === 'string'
        ? this.editor.getTrackById(trackOrId)
        : trackOrId;
  }

  execute(): boolean {
    if (!this.removedTrack) return false;

    // Store all elements in the track for undo operations
    this.trackElements =
      this.editor.timeline.getTrack(this.trackId)?.mutableElements || [];

    if (this.containsLayoutElements()) {
      VideoEditorLogger.logCommand(this, 'error', {
        error: 'Removing a track with layout elements is not supported.',
      });
      return false;
    }

    const success = this.editor.timeline.removeTrack(this.trackId);
    if (success) {
      this.editor.markDirty('remove_track_command');
      this.editor.notifyObservers(EditorChangeType.TRACK_REMOVED, {
        trackId: this.trackId,
        track: this.removedTrack,
      });
    }
    return success;
  }

  undo(): boolean {
    if (!this.removedTrack) return false;

    // Re-add the track
    const success = this.editor.timeline.addTrack(this.removedTrack);
    if (success) {
      // Re-add all elements that were in the track
      for (const element of this.trackElements) {
        this.editor.timeline.addElement(this.trackId, element);
      }

      this.editor.markDirty('remove_track_command_undo');
      this.editor.notifyObservers(EditorChangeType.TRACK_ADDED, {
        track: this.removedTrack,
      });

      // Notify about restored elements
      for (const element of this.trackElements) {
        this.editor.notifyObservers(EditorChangeType.ELEMENT_ADDED, {
          element,
          trackId: this.trackId,
        });
      }
    }
    return !!success;
  }

  redo(): boolean {
    return this.execute();
  }

  getDescription(): string {
    return `Remove track ${this.trackId}`;
  }

  getRemovedTrack(): Track | undefined {
    return this.removedTrack;
  }

  private containsLayoutElements(): boolean {
    return this.trackElements.some(element =>
      this.extraElementData.isLayoutElement(element),
    );
  }
}
